import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { track } from '../../../utils/analytics'
import { buildConsentFlowSteps, resolveUserConsentFlow } from '../../../utils/consentFlowHelper'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import type { ConsentContext } from '../consentOptions/types'

export function useConsentContext() {
  const apiClient = useFamilyPortalApiClient()
  const [context, setContext] = useState<ConsentContext | null>(null)
  const [contextLoading, setContextLoading] = useState(true)
  // Store a flag, not a translated string, so the message re-renders when locale switches.
  const [contextError, setContextError] = useState<boolean>(false)
  const [contextMissing, setContextMissing] = useState(false)
  const [reloadCount, setReloadCount] = useState(0)
  // A refresh keeps the current screen mounted instead of showing the loading panel.
  const silentReloadRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    if (!silentReloadRef.current) {
      setContextLoading(true)
      setContextError(false)
      setContextMissing(false)
    }
    silentReloadRef.current = false
    apiClient
      .get('/consents/context', undefined, true)
      .then((res) => {
        if (cancelled) return
        setContextLoading(false)
        if (res.status === 404) {
          setContextMissing(true)
          return
        }
        if (!res.ok) {
          setContextError(true)
          track('consent.load_error', { resource: 'context', status: res.status })
          return
        }
        return res.json()
      })
      .then((data: ConsentContext | undefined) => {
        if (cancelled || data === undefined) return
        setContext(data)
        setContextError(false)
        setContextMissing(false)
      })
      .catch(() => {
        if (!cancelled) {
          setContextLoading(false)
          setContextError(true)
          track('consent.load_error', { resource: 'context', status: 'network_error' })
        }
      })
    return () => {
      cancelled = true
    }
  }, [apiClient, reloadCount])

  const refresh = useCallback(() => {
    silentReloadRef.current = true
    setReloadCount((count) => count + 1)
  }, [])

  const hasContext = Boolean(
    context &&
    (context.testName ||
      context.patientName ||
      context.orderDate ||
      context.orderingPractitionerName ||
      context.orderingPractitionerNpi ||
      context.orderingOrganization)
  )

  const workflowSteps = useMemo(() => buildConsentFlowSteps(resolveUserConsentFlow(context)), [context])
  const dualSecondary = context?.secondaryConsentDualSubjects === true

  return {
    context,
    contextLoading,
    contextError,
    contextMissing,
    hasContext,
    workflowSteps,
    dualSecondary,
    refresh,
  }
}
